import { AlertTriangle, CheckCircle2, Clock, Info, MinusCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { t } from '../../lib/i18n';

export type StatusTone = 'good' | 'warn' | 'bad' | 'neutral' | 'info';

const STYLE: Record<StatusTone, { cls: string; icon: typeof Info }> = {
  good: { cls: 'bg-green-100 text-green-900', icon: CheckCircle2 },
  warn: { cls: 'bg-amber-100 text-amber-900', icon: Clock },
  bad: { cls: 'bg-red-100 text-red-900', icon: AlertTriangle },
  neutral: { cls: 'bg-slate-100 text-slate-700', icon: MinusCircle },
  info: { cls: 'bg-sky-100 text-sky-900', icon: Info },
};

/** Ready-made tones for common statuses. */
export const STATUS_TONE: Record<'active' | 'pending' | 'failed' | 'disabled' | 'info', { tone: StatusTone; labelKey: string }> = {
  active: { tone: 'good', labelKey: 'p2w.status.active' },
  pending: { tone: 'warn', labelKey: 'p2w.status.pending' },
  failed: { tone: 'bad', labelKey: 'p2w.status.failed' },
  disabled: { tone: 'neutral', labelKey: 'p2w.status.disabled' },
  info: { tone: 'info', labelKey: 'p2w.status.info' },
};

/**
 * Small rounded status label. Every tone has its own icon, so meaning never depends on colour alone.
 * Pass `status` for a standard word, or `tone` plus children for your own text.
 */
export function StatusPill({ status, tone, children }: { status?: keyof typeof STATUS_TONE; tone?: StatusTone; children?: ReactNode }) {
  const preset = status ? STATUS_TONE[status] : undefined;
  const use = STYLE[tone ?? preset?.tone ?? 'neutral'];
  const Icon = use.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${use.cls}`}>
      <Icon aria-hidden="true" className="h-3.5 w-3.5" />
      {children ?? (preset ? t(preset.labelKey) : null)}
    </span>
  );
}
